import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { Box, Slide, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import GridItem from '@jumbo/components/GridItem';
import ItemListView from 'Components/ItemListView';
import { ApplicationIcons } from 'icons/ApplicationIcons';
import GridContainer from '@jumbo/components/GridContainer';
import CartRedirectionModal from '../Components/CartRedirectionModal';

import { useToolings } from 'services/Data/ToolingServices';
import { useInventoryItems } from 'services/Data/InventoryItemServices';
import useImage from 'services/Hooks/useImage';
import useTableDataParams from 'services/Hooks/useTableDataParams';
import { useActiveCart, usePostCarItem, usePostCartTooling } from 'services/Data/CartService';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { LanguageEnumValue } from 'enums/LanguageEnum';
import InfiniteScroll from 'react-infinite-scroll-component';
import InventoryItemDealerPortalTypeEnum, {
  InventoryItemDealerPortalTypeEnumValue,
} from 'enums/InventoryItemDealerPortalTypeEnum';
import ListViewToggle from 'Components/ListViewToggle';
import { useSearchWalls } from 'services/Data/WallService';
import { IsImpersonationMode } from 'config/config';

const useStyles = makeStyles(() => ({
  pageFull: {
    width: '100%',
  },
}));

const useQueryParam = key => {
  const { search } = useLocation();
  return new URLSearchParams(search).get(key);
};

const SearchResults = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const search = useQueryParam('q') || '';

  const accountReducer = useSelector(state => state.AccountReducer);
  const { profile } = accountReducer;
  const dealerId = profile?.dealer_Id;

  const person = useMemo(() => profile?.person, [profile?.person]);

  const { data: cart } = useActiveCart(accountReducer.isLoggedIn, false, IsImpersonationMode ? dealerId : null);
  const [showRedirectionModal, setShowRedirectionModal] = useState(false);
  const [toolingsList, setToolingsList] = useState([]);
  const [inventoryItemsList, setInventoryItemsList] = useState([]);
  const [wallsList, setWallsList] = useState([]);
  const [isCardView, setIsCardView] = useState(true);
  const [combinedPage, setCombinedPage] = useState(1);
  const [hasJustMounted, setHasJustMounted] = useState(true);

  const [toolingTableDataParams, setToolingTableDataParams] = useTableDataParams();
  const [inventoryItemTableDataParams, setInventoryItemTableDataParams] = useTableDataParams();
  const [wallTableDataParams, setWallTableDataParams] = useTableDataParams();

  const { data: toolingsCurrentPage, isLoading: loadingToolings } = useToolings({
    ...toolingTableDataParams,
    currentPage: combinedPage,
  });
  const { data: inventoryItemsCurrentPage, isLoading: loadingInventory } = useInventoryItems({
    ...inventoryItemTableDataParams,
    currentPage: combinedPage,
  });

  const { data: wallsCurrentPage, isLoading: loadingWalls } = useSearchWalls({
    ...wallTableDataParams,
    currentPage: combinedPage,
  });

  const { mutate: addCartTooling } = usePostCartTooling(accountReducer.isDemo);
  const { mutate: addCartItem } = usePostCarItem(accountReducer.isDemo);
  const { getToolingImage, getInventoryItemImage, getPanelTypeImage } = useImage();

  const hasMore = (toolingsCurrentPage?.items?.length || 0) > 0 || (inventoryItemsCurrentPage?.items?.length || 0) > 0;

  useEffect(() => {
    if (
      toolingTableDataParams.search !== search ||
      inventoryItemTableDataParams.search !== search ||
      wallTableDataParams.search !== search
    ) {
      setToolingsList([]);
      setInventoryItemsList([]);
      setWallsList([]);
      setCombinedPage(1);

      const defaultSort = [
        {
          column: `dealerPortalTitle${person?.languageEnum === LanguageEnumValue.FRENCH ? 'Fr' : 'En'}`,
          asc: true,
        },
      ];

      setToolingTableDataParams({
        ...toolingTableDataParams,
        currentPage: 1,
        search,
        sorts: defaultSort,
      });

      setInventoryItemTableDataParams({
        ...inventoryItemTableDataParams,
        currentPage: 1,
        search,
        sorts: defaultSort,
      });

      setWallTableDataParams({
        ...inventoryItemTableDataParams,
        currentPage: 1,
        search,
        sorts: defaultSort,
      });
    }
  }, [
    inventoryItemTableDataParams,
    person?.languageEnum,
    search,
    setInventoryItemTableDataParams,
    setToolingTableDataParams,
    setWallTableDataParams,
    toolingTableDataParams,
    wallTableDataParams.search,
  ]);

  useEffect(() => {
    setHasJustMounted(false);
  }, []);

  useEffect(() => {
    if (combinedPage === 1) {
      setToolingsList([...(toolingsCurrentPage?.items || [])]);
      setInventoryItemsList([...(inventoryItemsCurrentPage?.items || [])]);
      setWallsList([...(wallsCurrentPage?.items || [])]);
    } else {
      setToolingsList(prev => [...prev, ...(toolingsCurrentPage?.items || [])]);
      setInventoryItemsList(prev => [...prev, ...(inventoryItemsCurrentPage?.items || [])]);
      setWallsList(prev => [...prev, ...(wallsCurrentPage?.items || [])]);
    }
  }, [combinedPage, toolingsCurrentPage?.items, inventoryItemsCurrentPage?.items, wallsCurrentPage?.items]);

  const handleAddToCartTooling = useCallback(
    tooling => {
      if (tooling.additionalToolings?.length > 0) {
        const route = getRouteFromItem(tooling);
        if (route) {
          history.push(route);
          return;
        }
      } else {
        addCartTooling({
          id: uuidv4(),
          tooling_Id: tooling.id,
          price: tooling.price,
          subTotal: tooling.price,
          quantity: 1,
          cart_Id: cart?.id,
        });
        setShowRedirectionModal(true);
      }
    },
    [addCartTooling, cart?.id, history],
  );

  const handleAddToCartInventory = useCallback(
    item => {
      addCartItem({
        id: uuidv4(),
        inventoryItem_Id: item.id,
        unitPrice: item.price,
        currencyName: item.currencyName,
        quantity: 1,
        cart_Id: cart?.id,
      });
      setShowRedirectionModal(true);
    },
    [addCartItem, cart?.id],
  );

  const handleAddToCartWall = useCallback(
    item => {
      const associatedInventoryItemDealerPortalTypeEnum = InventoryItemDealerPortalTypeEnum.find(
        c => c.value === InventoryItemDealerPortalTypeEnumValue.Walls,
      );

      history.push(
        `dealer/${associatedInventoryItemDealerPortalTypeEnum.route}/${item.panelType_Id ? item.panelType_Id : 'custom'}`,
      );
    },
    [history],
  );

  const handleViewChange = useCallback((_, view) => {
    setIsCardView(view === 'card');
  }, []);

  const combinedList = useMemo(() => {
    const withTypes = [
      ...toolingsList.map(t => ({ ...t, type: 'tooling' })),
      ...inventoryItemsList.map(i => ({ ...i, type: 'inventory' })),
      ...wallsList.map(i => ({ ...i, type: 'wall' })),
    ];

    const currentSort = toolingTableDataParams.sorts?.[0];
    if (!currentSort) return withTypes;

    return withTypes.sort((a, b) => {
      const aVal = a[currentSort.column]?.toString().toLowerCase() ?? '';
      const bVal = b[currentSort.column]?.toString().toLowerCase() ?? '';
      return aVal.localeCompare(bVal) * (currentSort.asc ? 1 : -1);
    });
  }, [toolingsList, inventoryItemsList, wallsList, toolingTableDataParams.sorts]);

  const fetchNextCombinedPage = () => {
    if (!hasJustMounted) {
      setCombinedPage(prev => prev + 1);
    }
  };

  const getRouteFromItem = item => {
    const typeEnumValue = item.dealerPortalTypeEnum;
    const found = InventoryItemDealerPortalTypeEnum.find(e => e.value === typeEnumValue);
    if (!found) return null;

    if (found.parentCategory) {
      const parent = InventoryItemDealerPortalTypeEnum.find(p => p.value === found.parentCategory);
      return parent ? `/dealer/${parent.route}/${found.route}/${item.id}` : `/dealer/${found.route}/${item.id}`;
    }

    return `/dealer/${found.route}/${item.id}`;
  };

  return (
    <Slide direction="up" in mountOnEnter unmountOnExit>
      <Box className={classes.pageFull} sx={{ padding: 4 }}>
        <CartRedirectionModal
          show={showRedirectionModal}
          onClose={() => setShowRedirectionModal(false)}
          redirectLink={'/dealer/cart'}
          title={t('itemAddedToCart')}
          message={t('itemAddedToCartText')}
        />
        <Typography variant="h1" style={{ textTransform: 'uppercase' }}>
          {!loadingToolings && !loadingInventory && !loadingWalls && combinedList.length === 0
            ? t('noResultsFoundFor')
            : t('searchResultsFor')}
          <strong> "{search}"</strong>
        </Typography>
        {combinedList.length > 0 && (
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <ListViewToggle isCardView={isCardView} handleViewChange={handleViewChange} />
          </Box>
        )}
        <InfiniteScroll
          key={search}
          dataLength={combinedList.length}
          hasMore={hasMore}
          next={fetchNextCombinedPage}
          onScroll={() => {
            if (hasJustMounted) {
              setHasJustMounted(false);
            }
          }}
          endMessage={
            combinedList.length > 0 && (
              <GridItem xs={12} textAlign={'center'} sx={{ py: 3 }}>
                <b>{t('attained_end_items')}</b>
              </GridItem>
            )
          }>
          <GridContainer sx={{ mt: 1 }}>
            {combinedList.map(item => {
              const existingCartItem = cart?.cartItems?.find(ci => ci.inventoryItem_Id === item.id);
              const quantityInCart = existingCartItem?.quantity ?? 0;
              const isPromoLimitReached = item.isInPromotion && quantityInCart >= item.promotionQuantity;
              const isInventoriedLimitReached =
                item.inventoriedQuantityAvailable != null && quantityInCart >= item.inventoriedQuantityAvailable;

              return (
                <GridItem xs={12} md={isCardView ? 3 : 12} key={`${item.type}-${item.id}`}>
                  <ItemListView
                    onClick={() => {
                      const route = getRouteFromItem(item);
                      if (route) history.push(route);
                    }}
                    view={isCardView ? 'card' : 'list'}
                    title={item.name || item.dealerPortalTitleFr || item.dealerPortalTitleEn}
                    identification={item.sku}
                    description={item.shortDescription}
                    quantityPerPackage={item.quantityPerPackage}
                    imgSrc={
                      item.images?.length
                        ? item.type === 'tooling'
                          ? getToolingImage(
                              item.id,
                              item?.images.find(i => i.dealerPortalPrimary)
                                ? item?.images.find(i => i.dealerPortalPrimary).id
                                : item?.images[0].id,
                            )
                          : item.type === 'wall'
                          ? getPanelTypeImage(
                              item.panelType_Id,
                              item?.images.find(i => i.dealerPortalPrimary)
                                ? item?.images.find(i => i.dealerPortalPrimary).id
                                : item?.images[0].id,
                            )
                          : getInventoryItemImage(
                              item.id,
                              item?.images.find(i => i.dealerPortalPrimary)
                                ? item?.images.find(i => i.dealerPortalPrimary).id
                                : item?.images[0].id,
                            )
                        : '/images/brp-logo.png'
                    }
                    price={item.price}
                    promotionPrice={item.promotionPrice}
                    currency={item.currencyName}
                    promotionQuantity={item.promotionQuantity}
                    inventoriedQuantityAvailable={item.inventoriedQuantityAvailable}
                    actions={
                      item.type === 'tooling'
                        ? item.additionalToolings?.length > 0
                          ? [
                              {
                                icon: ApplicationIcons.Favorite,
                                color: 'secondary',
                                text: t('setUp'),
                                onClick: () => {
                                  const route = getRouteFromItem(item);
                                  if (route) {
                                    history.push(route);
                                  }
                                },
                              },
                            ]
                          : [
                              {
                                icon: ApplicationIcons.Favorite,
                                color: 'secondary',
                                text: t('addToCart'),
                                onClick: () => handleAddToCartTooling(item),
                                disabled: isPromoLimitReached || isInventoriedLimitReached,
                              },
                            ]
                        : item.type === 'wall'
                        ? [
                            {
                              icon: ApplicationIcons.Favorite,
                              color: 'secondary',
                              text: t('setUp'),
                              onClick: () => handleAddToCartWall(item),
                              disabled: isPromoLimitReached || isInventoriedLimitReached,
                            },
                          ]
                        : [
                            {
                              icon: ApplicationIcons.Favorite,
                              color: 'secondary',
                              text: t('addToCart'),
                              onClick: () => handleAddToCartInventory(item),
                              disabled: isPromoLimitReached || isInventoriedLimitReached,
                            },
                          ]
                    }
                  />
                </GridItem>
              );
            })}
          </GridContainer>
        </InfiniteScroll>
      </Box>
    </Slide>
  );
};

export default SearchResults;
